import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { CustomerOverviewTab } from "@/components/agency/customer/CustomerOverviewTab";
import { CustomerSettingsTab } from "@/components/agency/customer/CustomerSettingsTab";
import { CustomerUsersTab } from "@/components/agency/customer/CustomerUsersTab";
import { CustomerActivityTab } from "@/components/agency/customer/CustomerActivityTab";

const statusColors: Record<string, string> = {
  active: "bg-green-500/10 text-green-500 border-green-500/20",
  suspended: "bg-red-500/10 text-red-500 border-red-500/20",
  pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
  deleted: "bg-muted text-muted-foreground border-muted",
};

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: tenantData, isLoading, error } = useQuery({
    queryKey: ["tenant", id],
    queryFn: async () => {
      // Use POST with body since Supabase functions.invoke doesn't support query params well
      const { data, error } = await supabase.functions.invoke("Calie_getTenant", {
        body: { tenant_id: id },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const suspendMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_suspendTenant", {
        body: { tenant_id: id },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", id] });
      toast.success("Customer suspended");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to suspend customer");
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_reactivateTenant", {
        body: { tenant_id: id },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", id] });
      toast.success("Customer reactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reactivate customer");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_deleteTenant", {
        body: { tenant_id: id },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Customer deleted");
      navigate("/agency/customers");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete customer");
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_impersonateTenant", {
        body: { tenant_id: id },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Impersonating ${data.tenant?.name || "customer"}`);
      // Open in new tab with impersonation token
      const url = new URL(data.redirect_url, window.location.origin);
      url.searchParams.set("impersonate", data.token);
      window.open(url.toString(), "_blank");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to start impersonation");
    },
  });

  const isActionLoading =
    suspendMutation.isPending ||
    reactivateMutation.isPending ||
    deleteMutation.isPending ||
    impersonateMutation.isPending;

  if (isLoading) {
    return (
      <MainLayout>
        <div className="space-y-6 p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-[400px] w-full" />
        </div>
      </MainLayout>
    );
  }

  if (error || !tenantData?.tenant) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-24">
          <AlertCircle className="h-16 w-16 text-destructive mb-4" />
          <h2 className="text-xl font-semibold">Customer Not Found</h2>
          <p className="text-muted-foreground mt-2">
            The customer you're looking for doesn't exist or you don't have access.
          </p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => navigate("/agency/customers")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Customers
          </Button>
        </div>
      </MainLayout>
    );
  }

  const tenant = tenantData.tenant;

  return (
    <MainLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/agency/customers")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{tenant.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm text-muted-foreground">
                  {tenant.slug}
                </code>
                <span className="text-muted-foreground">•</span>
                <Badge
                  variant="outline"
                  className={statusColors[tenant.status || "active"]}
                >
                  {tenant.status || "active"}
                </Badge>
                {tenant.plan_id && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">
                      {tenant.plan_id}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <CustomerOverviewTab
              tenant={tenant}
              onSuspend={() => suspendMutation.mutate()}
              onReactivate={() => reactivateMutation.mutate()}
              onDelete={() => deleteMutation.mutate()}
              onImpersonate={() => impersonateMutation.mutate()}
              isLoading={isActionLoading}
            />
          </TabsContent>

          <TabsContent value="settings">
            <CustomerSettingsTab tenant={tenant} />
          </TabsContent>

          <TabsContent value="users">
            <CustomerUsersTab tenantId={tenant.tenant_id} />
          </TabsContent>

          <TabsContent value="activity">
            <CustomerActivityTab tenantId={tenant.tenant_id} />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
